import { useState, useMemo, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SaveIcon from '@mui/icons-material/Save';
import SortIcon from '@mui/icons-material/Sort';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { fetchWeeklySummary, confirmPayout, fetchPayoutHistory, deletePayout } from '../../api/accounting';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import { getWeekStartDate, formatWeekRangeLabel } from '../../utils/week';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444'];
const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#EDE9FE' }, '&.Mui-focused fieldset': { borderColor: B.primary } },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const fmtHours = (v) => parseFloat(v).toFixed(2);

const HISTORY_SORT_OPTIONS = [
  { value: 'newest', label: 'Найновіші' },
  { value: 'oldest', label: 'Найстаріші' },
  { value: 'amount_desc', label: 'Більша сума' },
  { value: 'amount_asc', label: 'Менша сума' },
  { value: 'hours_desc', label: 'Більше годин' },
];

const sortHistory = (data, sortBy) => {
  const sorted = [...data];
  switch (sortBy) {
    case 'newest': return sorted.sort((a, b) => dayjs(b.weekStart).valueOf() - dayjs(a.weekStart).valueOf());
    case 'oldest': return sorted.sort((a, b) => dayjs(a.weekStart).valueOf() - dayjs(b.weekStart).valueOf());
    case 'amount_desc': return sorted.sort((a, b) => parseFloat(b.totalAmount) - parseFloat(a.totalAmount));
    case 'amount_asc': return sorted.sort((a, b) => parseFloat(a.totalAmount) - parseFloat(b.totalAmount));
    case 'hours_desc': return sorted.sort((a, b) => parseFloat(b.hoursWorked) - parseFloat(a.hoursWorked));
    default: return sorted;
  }
};

const AdminAccountingPage = () => {
  const queryClient = useQueryClient();
  const normalizeWeek = (value) => getWeekStartDate(value).format('YYYY-MM-DD');
  const [weekStart, setWeekStart] = useState(normalizeWeek());
  const [view, setView] = useState('weekly');
  const [rates, setRates] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Earnings (profit)
  const [tutorRates, setTutorRates] = useState({});
  const [lessonPrices, setLessonPrices] = useState({});

  // Report filters
  const [reportTutor, setReportTutor] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  // History filters
  const [historySort, setHistorySort] = useState('newest');
  const [historyTutor, setHistoryTutor] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const weekEnd = dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD');

  // Reset local rate state when week changes so backend values take effect
  useEffect(() => {
    setRates({});
    setTutorRates({});
    setLessonPrices({});
  }, [weekStart]);

  const summaryQuery = useQuery({
    queryKey: ['accounting-summary', weekStart],
    queryFn: () => fetchWeeklySummary(weekStart),
    staleTime: 30_000,
  });

  const historyQuery = useQuery({
    queryKey: ['accounting-history'],
    queryFn: fetchPayoutHistory,
    enabled: view === 'history' || view === 'reports',
    staleTime: 30_000,
  });

  const payoutMutation = useMutation({
    mutationFn: confirmPayout,
    onSuccess: () => {
      enqueueSnackbar('Виплату підтверджено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['accounting-summary'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-history'] });
    },
    onError: () => enqueueSnackbar('Не вдалося зберегти виплату', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePayout,
    onSuccess: () => {
      enqueueSnackbar('Виплату видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['accounting-summary'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-history'] });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити', { variant: 'error' }),
  });

  const getRate = (tutorId, defaultRate) => rates[tutorId] ?? defaultRate ?? '';
  const setRate = (tutorId, value) => {
    setRates((prev) => ({ ...prev, [tutorId]: value }));
    setTutorRates((prev) => ({ ...prev, [tutorId]: value }));
  };
  const calcTotal = (hours, rate) => ((parseFloat(hours) || 0) * (parseFloat(rate) || 0)).toFixed(2);

  const handleConfirmPayout = (tutor) => {
    const rate = parseFloat(getRate(tutor.tutorId, tutor.hourlyRate)) || 0;
    const price = parseFloat(lessonPrices[tutor.tutorId]) || 0;
    const hours = parseFloat(tutor.hoursWorked) || 0;
    const earning = price > rate ? (price - rate) * hours : 0;
    payoutMutation.mutate({
      tutorId: tutor.tutorId, weekStart, weekEnd,
      completedLessons: tutor.completedLessons,
      hoursWorked: tutor.hoursWorked,
      hourlyRate: rate,
      totalAmount: parseFloat(calcTotal(tutor.hoursWorked, rate)),
      lessonPrice: price || null,
      schoolEarning: parseFloat(earning.toFixed(2)) || null,
    });
  };

  const handleConfirmEarning = (tutor) => {
    const rate = parseFloat(tutorRates[tutor.tutorId] || getRate(tutor.tutorId, tutor.hourlyRate)) || 0;
    const price = parseFloat(lessonPrices[tutor.tutorId]) || 0;
    const hours = parseFloat(tutor.hoursWorked) || 0;
    const earning = price > rate ? (price - rate) * hours : 0;
    payoutMutation.mutate({
      tutorId: tutor.tutorId, weekStart, weekEnd,
      completedLessons: tutor.completedLessons,
      hoursWorked: tutor.hoursWorked,
      hourlyRate: rate,
      totalAmount: parseFloat(calcTotal(tutor.hoursWorked, rate)),
      lessonPrice: price,
      schoolEarning: parseFloat(earning.toFixed(2)),
    });
  };

  const summaryData = summaryQuery.data || [];

  // Auto-populate rates and lesson prices from backend (saved or last-used values)
  useEffect(() => {
    if (!summaryData.length) return;
    const newRates = {};
    const newTutorRates = {};
    const newPrices = {};
    summaryData.forEach((t) => {
      if (t.hourlyRate && parseFloat(t.hourlyRate) > 0) {
        newRates[t.tutorId] = String(t.hourlyRate);
        newTutorRates[t.tutorId] = String(t.hourlyRate);
      }
      if (t.lessonPrice && parseFloat(t.lessonPrice) > 0) {
        newPrices[t.tutorId] = String(t.lessonPrice);
      }
    });
    setRates((prev) => ({ ...newRates, ...prev }));
    setTutorRates((prev) => ({ ...newTutorRates, ...prev }));
    setLessonPrices((prev) => ({ ...newPrices, ...prev }));
  }, [summaryQuery.data]);

  const totalWeekAmount = useMemo(() =>
    summaryData.reduce((sum, t) => sum + parseFloat(calcTotal(t.hoursWorked, getRate(t.tutorId, t.hourlyRate))), 0),
    [summaryData, rates]
  );

  const historyData = historyQuery.data || [];

  // Unique tutors for filter dropdown
  const uniqueTutors = useMemo(() => {
    const map = new Map();
    historyData.forEach((p) => { if (!map.has(p.tutorId)) map.set(p.tutorId, p.tutorName); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [historyData]);

  // Filtered + sorted history
  const filteredHistory = useMemo(() => {
    let data = [...historyData];
    if (historyTutor) data = data.filter((p) => String(p.tutorId) === historyTutor);
    if (historyDateFrom) data = data.filter((p) => p.weekStart >= historyDateFrom);
    if (historyDateTo) data = data.filter((p) => p.weekStart <= historyDateTo);
    return sortHistory(data, historySort);
  }, [historyData, historyTutor, historyDateFrom, historyDateTo, historySort]);

  const filteredTotal = useMemo(() => filteredHistory.reduce((s, p) => s + parseFloat(p.totalAmount), 0), [filteredHistory]);

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 2.5, px: { xs: 2, md: 3.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>Бухгалтерія</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                Зарплати репетиторів та історія виплат
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={view} exclusive onChange={(_, v) => { if (v) setView(v); }} size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid rgba(255,255,255,0.3)', borderRadius: '20px !important', px: 2, py: 0.5, textTransform: 'none', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } } } }}
            >
              <ToggleButton value="weekly"><MonetizationOnIcon sx={{ fontSize: 16, mr: 0.5 }} /> Тиждень</ToggleButton>
              <ToggleButton value="history"><HistoryIcon sx={{ fontSize: 16, mr: 0.5 }} /> Історія</ToggleButton>
              <ToggleButton value="reports"><SummarizeIcon sx={{ fontSize: 16, mr: 0.5 }} /> Звіти</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>
      </Card>

      {/* ═══ WEEKLY VIEW ═══ */}
      {view === 'weekly' && (
        <>
          <Card sx={cardSx}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                <IconButton size="small" onClick={() => setWeekStart(normalizeWeek(dayjs(weekStart).subtract(1, 'week')))}><ChevronLeftIcon /></IconButton>
                <Stack alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark }}>{formatWeekRangeLabel(weekStart)}</Typography>
                  <Button size="small" onClick={() => setWeekStart(normalizeWeek())} sx={{ textTransform: 'none', fontSize: 12, color: B.primary }}>Поточний тиждень</Button>
                </Stack>
                <IconButton size="small" onClick={() => setWeekStart(normalizeWeek(dayjs(weekStart).add(1, 'week')))}><ChevronRightIcon /></IconButton>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent sx={{ py: 2 }}>
              {summaryQuery.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
              ) : summaryData.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Немає даних за цей тиждень</Typography></Box>
              ) : (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #EDE9FE', py: 1.5 } }}>
                          <TableCell>Репетитор</TableCell>
                          <TableCell align="center">Занять</TableCell>
                          <TableCell align="center">Годин</TableCell>
                          <TableCell align="center">Ставка (₴/год)</TableCell>
                          <TableCell align="center">До виплати (₴)</TableCell>
                          <TableCell align="center">Статус</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summaryData.map((tutor, i) => {
                          const rate = getRate(tutor.tutorId, tutor.hourlyRate);
                          const total = calcTotal(tutor.hoursWorked, rate);
                          const color = avatarColors[i % avatarColors.length];
                          return (
                            <TableRow key={tutor.tutorId} sx={{ '&:hover': { bgcolor: '#FAFAFF' }, '& td': { py: 1.5, borderBottom: '1px solid #F1F0FB' } }}>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <Avatar sx={{ width: 34, height: 34, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 14 }}>
                                    {tutor.tutorName?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{tutor.tutorName}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={tutor.completedLessons} size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 13, height: 26 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>{fmtHours(tutor.hoursWorked)}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small" type="number" value={rate}
                                  onChange={(e) => setRate(tutor.tutorId, e.target.value)}
                                  inputProps={{ min: 0, step: 50, style: { textAlign: 'center', width: 80 } }}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  disabled={tutor.payoutConfirmed}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight={800} sx={{ color: '#22C55E', fontSize: 16 }}>{total}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                {tutor.payoutConfirmed ? (
                                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                    <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} label="Підтверджено" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 12 }} />
                                    <Tooltip title="Скасувати виплату">
                                      <IconButton size="small" onClick={() => setDeleteConfirm({ id: tutor.payoutId, tutorName: tutor.tutorName, weekStart, weekEnd, totalAmount: total })}
                                        sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ) : (
                                  <Button size="small" variant="contained" startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => handleConfirmPayout(tutor)} disabled={payoutMutation.isPending || !rate}
                                    sx={{ bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>
                                    Підтвердити
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                    <Typography variant="body2" sx={{ color: '#8891A5' }}>Загалом за тиждень:</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: B.primary }}>{totalWeekAmount.toFixed(2)} ₴</Typography>
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>

          {/* ═══ EARNINGS TABLE ═══ */}
          {summaryData.length > 0 && (
            <Card sx={cardSx}>
              <CardContent sx={{ py: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#D1FAE5', color: '#22C55E' }}>
                    <MonetizationOnIcon />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>Заробіток школи</Typography>
                </Stack>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #EDE9FE', py: 1.5 } }}>
                        <TableCell>Репетитор</TableCell>
                        <TableCell align="center">Занять</TableCell>
                        <TableCell align="center">Ставка репетитора (₴/год)</TableCell>
                        <TableCell align="center">Вартість заняття (₴/год)</TableCell>
                        <TableCell align="center">Заробіток (₴)</TableCell>
                        <TableCell align="center">Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summaryData.map((tutor, i) => {
                        const tutorRate = tutorRates[tutor.tutorId] ?? '';
                        const price = lessonPrices[tutor.tutorId] ?? '';
                        const r = parseFloat(tutorRate) || 0;
                        const p = parseFloat(price) || 0;
                        const hours = parseFloat(tutor.hoursWorked) || 0;
                        const earning = p > r ? ((p - r) * hours).toFixed(2) : '0.00';
                        const color = avatarColors[i % avatarColors.length];
                        const earningConfirmed = tutor.payoutConfirmed && tutor.lessonPrice != null;
                        return (
                          <TableRow key={tutor.tutorId} sx={{ '&:hover': { bgcolor: '#FAFAFF' }, '& td': { py: 1.5, borderBottom: '1px solid #F1F0FB' } }}>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{ width: 34, height: 34, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 14 }}>
                                  {tutor.tutorName?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{tutor.tutorName}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>{tutor.completedLessons}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number" value={tutorRate}
                                onChange={(e) => { setTutorRates((prev) => ({ ...prev, [tutor.tutorId]: e.target.value })); setRates((prev) => ({ ...prev, [tutor.tutorId]: e.target.value })); }}
                                inputProps={{ min: 0, step: 50, style: { textAlign: 'center', width: 80 } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                disabled={earningConfirmed}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number" value={price}
                                onChange={(e) => setLessonPrices((prev) => ({ ...prev, [tutor.tutorId]: e.target.value }))}
                                inputProps={{ min: 0, step: 50, style: { textAlign: 'center', width: 80 } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                disabled={earningConfirmed}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={800} sx={{ color: parseFloat(earning) > 0 ? '#22C55E' : '#8891A5', fontSize: 16 }}>
                                {earning}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {earningConfirmed ? (
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                  <Chip icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} label="Підтверджено" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 12 }} />
                                  <Tooltip title="Скасувати">
                                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: tutor.payoutId, tutorName: tutor.tutorName, weekStart, weekEnd, totalAmount: earning })}
                                      sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Button size="small" variant="contained" startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => handleConfirmEarning(tutor)} disabled={payoutMutation.isPending || !tutorRate || !price}
                                  sx={{ bgcolor: '#22C55E', '&:hover': { bgcolor: '#16A34A' }, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 12 }}>
                                  Підтвердити
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>Загальний заробіток:</Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#22C55E' }}>
                    {summaryData.reduce((sum, t) => {
                      const r = parseFloat(tutorRates[t.tutorId]) || 0;
                      const p = parseFloat(lessonPrices[t.tutorId]) || 0;
                      const h = parseFloat(t.hoursWorked) || 0;
                      return sum + (p > r ? (p - r) * h : 0);
                    }, 0).toFixed(2)} ₴
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ═══ HISTORY VIEW ═══ */}
      {view === 'history' && (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#EDE9FE', color: B.primary }}>
                <HistoryIcon />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>Історія виплат</Typography>
              {filteredHistory.length > 0 && (
                <Chip label={filteredHistory.length} size="small" sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 700, fontSize: 12 }} />
              )}
            </Stack>

            {/* Filters + Sort */}
            <Box sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  select size="small" label="Репетитор" value={historyTutor}
                  onChange={(e) => setHistoryTutor(e.target.value)}
                  sx={{ ...fieldSx, flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 160 }}
                >
                  <MenuItem value="">Усі репетитори</MenuItem>
                  {uniqueTutors.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                </TextField>
                <DatePicker
                  label="Від" format="DD.MM.YYYY"
                  value={historyDateFrom ? dayjs(historyDateFrom) : null}
                  onChange={(val) => setHistoryDateFrom(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { size: 'small', sx: { ...fieldSx, flex: { xs: '1 1 45%', sm: '0 0 auto' }, minWidth: 150 } } }}
                />
                <DatePicker
                  label="До" format="DD.MM.YYYY"
                  value={historyDateTo ? dayjs(historyDateTo) : null}
                  onChange={(val) => setHistoryDateTo(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { size: 'small', sx: { ...fieldSx, flex: { xs: '1 1 45%', sm: '0 0 auto' }, minWidth: 150 } } }}
                />
                {(historyTutor || historyDateFrom || historyDateTo) && (
                  <Button size="small" onClick={() => { setHistoryTutor(''); setHistoryDateFrom(''); setHistoryDateTo(''); }}
                    sx={{ textTransform: 'none', color: '#EF4444', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    Скинути
                  </Button>
                )}
              </Stack>
              <Divider sx={{ my: 1.5, borderColor: '#EDE9FE' }} />
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <SortIcon sx={{ fontSize: 16, color: '#8891A5' }} />
                <ToggleButtonGroup
                  value={historySort} exclusive onChange={(_, v) => { if (v) setHistorySort(v); }} size="small"
                  sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButton-root': { border: '1px solid #ddd6fe', borderRadius: '16px !important', px: { xs: 1, sm: 1.5 }, py: 0.25, textTransform: 'none', fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: '#8891A5', '&.Mui-selected': { bgcolor: B.primary, color: '#fff', borderColor: B.primary, '&:hover': { bgcolor: '#3648A0' } } } }}
                >
                  {HISTORY_SORT_OPTIONS.map((opt) => <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Stack>
            </Box>

            {historyQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
            ) : filteredHistory.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Виплат не знайдено</Typography></Box>
            ) : (
              <>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #EDE9FE', py: 1.5 } }}>
                        <TableCell>Тиждень</TableCell>
                        <TableCell>Репетитор</TableCell>
                        <TableCell align="center">Занять</TableCell>
                        <TableCell align="center">Годин</TableCell>
                        <TableCell align="center">Ставка</TableCell>
                        <TableCell align="center">Сума</TableCell>
                        <TableCell align="center">Заробіток школи</TableCell>
                        <TableCell>Примітки</TableCell>
                        <TableCell align="center">Підтверджено</TableCell>
                        <TableCell align="right">Дії</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.map((payout, i) => (
                        <TableRow key={payout.id} sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#FAFAFF', '&:hover': { bgcolor: '#EDE9FE' }, '& td': { py: 1.5, borderBottom: '1px solid #F1F0FB' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>
                              {dayjs(payout.weekStart).format('DD.MM')} – {dayjs(payout.weekEnd).format('DD.MM.YY')}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>{payout.tutorName}</Typography></TableCell>
                          <TableCell align="center">{payout.completedLessons}</TableCell>
                          <TableCell align="center">{fmtHours(payout.hoursWorked)}</TableCell>
                          <TableCell align="center">{payout.hourlyRate} ₴</TableCell>
                          <TableCell align="center"><Typography variant="body2" fontWeight={700} sx={{ color: '#22C55E' }}>{payout.totalAmount} ₴</Typography></TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={700} sx={{ color: parseFloat(payout.schoolEarning || 0) > 0 ? '#22C55E' : '#8891A5' }}>
                              {parseFloat(payout.schoolEarning || 0) > 0 ? `${parseFloat(payout.schoolEarning).toFixed(2)} ₴` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: '#8891A5', maxWidth: 120 }} noWrap>{payout.notes || '—'}</Typography></TableCell>
                          <TableCell align="center"><Typography variant="caption" sx={{ color: '#8891A5' }}>{payout.confirmedAt ? dayjs(payout.confirmedAt).format('DD.MM.YY HH:mm') : '—'}</Typography></TableCell>
                          <TableCell align="right">
                            <Tooltip title="Видалити">
                              <IconButton size="small" onClick={() => setDeleteConfirm(payout)} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>
                    Загалом{historyTutor || historyDateFrom || historyDateTo ? ' (фільтр)' : ''}:
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: B.primary }}>{filteredTotal.toFixed(2)} ₴</Typography>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ REPORTS VIEW ═══ */}
      {view === 'reports' && (() => {
        const all = historyData;
        // Filters
        let filtered = [...all];
        if (reportTutor) filtered = filtered.filter((p) => String(p.tutorId) === reportTutor);
        if (reportDateFrom) filtered = filtered.filter((p) => p.weekStart >= reportDateFrom);
        if (reportDateTo) filtered = filtered.filter((p) => p.weekStart <= reportDateTo);
        filtered.sort((a, b) => dayjs(b.weekStart).valueOf() - dayjs(a.weekStart).valueOf());

        // Unique tutors
        const tutorMap = new Map();
        all.forEach((p) => { if (!tutorMap.has(p.tutorId)) tutorMap.set(p.tutorId, p.tutorName); });
        const tutorOptions = [...tutorMap.entries()].map(([id, name]) => ({ id, name }));

        // Per-tutor aggregation
        const perTutor = new Map();
        filtered.forEach((p) => {
          const key = p.tutorId;
          if (!perTutor.has(key)) perTutor.set(key, { tutorName: p.tutorName, lessons: 0, hours: 0, totalPaid: 0, totalEarning: 0, avgRate: 0, count: 0 });
          const t = perTutor.get(key);
          t.lessons += p.completedLessons;
          t.hours += parseFloat(p.hoursWorked);
          t.totalPaid += parseFloat(p.totalAmount);
          t.totalEarning += parseFloat(p.schoolEarning || 0);
          t.avgRate += parseFloat(p.hourlyRate);
          t.count += 1;
        });
        perTutor.forEach((t) => { t.avgRate = t.count > 0 ? t.avgRate / t.count : 0; });
        const perTutorArr = [...perTutor.entries()].map(([id, d]) => ({ tutorId: id, ...d }));

        // Totals
        const totalPaid = filtered.reduce((s, p) => s + parseFloat(p.totalAmount), 0);
        const totalEarning = filtered.reduce((s, p) => s + parseFloat(p.schoolEarning || 0), 0);
        const totalHours = filtered.reduce((s, p) => s + parseFloat(p.hoursWorked), 0);
        const totalLessons = filtered.reduce((s, p) => s + p.completedLessons, 0);

        // Weeks count
        const uniqueWeeks = new Set(filtered.map((p) => p.weekStart));

        return (
          <Stack spacing={2.5}>
            {/* Filters */}
            <Card sx={cardSx}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                  <TextField
                    select size="small" label="Репетитор" value={reportTutor}
                    onChange={(e) => setReportTutor(e.target.value)}
                    sx={{ ...fieldSx, minWidth: 180 }}
                  >
                    <MenuItem value="">Усі репетитори</MenuItem>
                    {tutorOptions.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                  </TextField>
                  <DatePicker
                    label="Від" format="DD.MM.YYYY"
                    value={reportDateFrom ? dayjs(reportDateFrom) : null}
                    onChange={(val) => setReportDateFrom(val ? val.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', sx: { ...fieldSx, minWidth: 150 } } }}
                  />
                  <DatePicker
                    label="До" format="DD.MM.YYYY"
                    value={reportDateTo ? dayjs(reportDateTo) : null}
                    onChange={(val) => setReportDateTo(val ? val.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', sx: { ...fieldSx, minWidth: 150 } } }}
                  />
                  {(reportTutor || reportDateFrom || reportDateTo) && (
                    <Button size="small" onClick={() => { setReportTutor(''); setReportDateFrom(''); setReportDateTo(''); }}
                      sx={{ textTransform: 'none', color: '#EF4444', fontSize: 12, fontWeight: 600 }}>
                      Скинути
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Summary cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              {[
                { label: 'Загальні виплати', value: `${totalPaid.toFixed(2)} ₴`, color: B.primary, bg: '#EDE9FE' },
                { label: 'Заробіток школи', value: `${totalEarning.toFixed(2)} ₴`, color: '#22C55E', bg: '#D1FAE5' },
                { label: 'Всього годин', value: fmtHours(totalHours), color: '#06B6D4', bg: '#CFFAFE' },
                { label: 'Всього занять', value: totalLessons, color: '#F59E0B', bg: '#FEF3C7' },
              ].map((s) => (
                <Card key={s.label} sx={{ ...cardSx, height: '100%' }}>
                  <CardContent sx={{ py: 2.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={800} sx={{ color: s.color, mb: 0.5 }}>{s.value}</Typography>
                    <Typography variant="body2" sx={{ color: '#8891A5', fontWeight: 500 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Per-tutor breakdown */}
            <Card sx={cardSx}>
              <CardContent sx={{ py: 2.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: B.dark, mb: 2 }}>Виплати по репетиторах</Typography>
                {historyQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: B.primary }} /></Box>
                ) : perTutorArr.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Немає даних</Typography></Box>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #EDE9FE', py: 1.5 } }}>
                          <TableCell>Репетитор</TableCell>
                          <TableCell align="center">Занять</TableCell>
                          <TableCell align="center">Годин</TableCell>
                          <TableCell align="center">Сер. ставка (₴/год)</TableCell>
                          <TableCell align="center">Виплачено (₴)</TableCell>
                          <TableCell align="center">Заробіток школи (₴)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {perTutorArr.map((t, i) => {
                          const color = avatarColors[i % avatarColors.length];
                          return (
                            <TableRow key={t.tutorId} sx={{ '&:hover': { bgcolor: '#FAFAFF' }, '& td': { py: 1.5, borderBottom: '1px solid #F1F0FB' } }}>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <Avatar sx={{ width: 34, height: 34, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 14 }}>
                                    {t.tutorName?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{t.tutorName}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="center">{t.lessons}</TableCell>
                              <TableCell align="center">{fmtHours(t.hours)}</TableCell>
                              <TableCell align="center">{t.avgRate.toFixed(0)} ₴</TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight={700} sx={{ color: B.primary }}>{t.totalPaid.toFixed(2)} ₴</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight={700} sx={{ color: '#22C55E' }}>{t.totalEarning.toFixed(2)} ₴</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" alignItems={{ sm: 'center' }} spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ color: '#8891A5' }}>Виплачено:</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: B.primary }}>{totalPaid.toFixed(2)} ₴</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ color: '#8891A5' }}>Заробіток школи:</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#22C55E' }}>{totalEarning.toFixed(2)} ₴</Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Weekly breakdown */}
            <Card sx={cardSx}>
              <CardContent sx={{ py: 2.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: B.dark, mb: 2 }}>Виплати по тижнях</Typography>
                {(() => {
                  const weekMap = new Map();
                  filtered.forEach((p) => {
                    const key = p.weekStart;
                    if (!weekMap.has(key)) weekMap.set(key, { weekStart: p.weekStart, weekEnd: p.weekEnd, payouts: [], total: 0, earning: 0, hours: 0, lessons: 0 });
                    const w = weekMap.get(key);
                    w.payouts.push(p);
                    w.total += parseFloat(p.totalAmount);
                    w.earning += parseFloat(p.schoolEarning || 0);
                    w.hours += parseFloat(p.hoursWorked);
                    w.lessons += p.completedLessons;
                  });
                  const weeks = [...weekMap.values()].sort((a, b) => dayjs(b.weekStart).valueOf() - dayjs(a.weekStart).valueOf());

                  if (weeks.length === 0) {
                    return <Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Немає даних</Typography></Box>;
                  }

                  return (
                    <Stack spacing={1.5}>
                      {weeks.map((w) => (
                        <Box key={w.weekStart} sx={{ p: 2, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                              {dayjs(w.weekStart).format('DD.MM')} – {dayjs(w.weekEnd).format('DD.MM.YYYY')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Chip label={`${w.lessons} занять`} size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 12 }} />
                              <Chip label={`${fmtHours(w.hours)} год`} size="small" sx={{ bgcolor: '#CFFAFE', color: '#0E7490', fontWeight: 600, fontSize: 12 }} />
                              <Typography variant="subtitle2" fontWeight={800} sx={{ color: B.primary }}>{w.total.toFixed(2)} ₴</Typography>
                              {w.earning > 0 && (
                                <Chip label={`+${w.earning.toFixed(2)} ₴`} size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 11 }} />
                              )}
                            </Stack>
                          </Stack>
                          <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
                            {w.payouts.map((p) => (
                              <Stack key={p.id} direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                                <Typography variant="caption" fontWeight={600} sx={{ color: B.dark, minWidth: 120 }}>{p.tutorName}</Typography>
                                <Typography variant="caption" sx={{ color: '#8891A5' }}>{fmtHours(p.hoursWorked)} год × {p.hourlyRate} ₴</Typography>
                                <Typography variant="caption" fontWeight={700} sx={{ color: B.primary }}>= {p.totalAmount} ₴</Typography>
                                {parseFloat(p.schoolEarning || 0) > 0 && (
                                  <Chip label={`+${parseFloat(p.schoolEarning).toFixed(2)} ₴`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#D1FAE5', color: '#065F46' }} />
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>
          </Stack>
        );
      })()}

      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        title="Видалити виплату?"
        description={deleteConfirm ? `${deleteConfirm.tutorName} — ${dayjs(deleteConfirm.weekStart).format('DD.MM')}–${dayjs(deleteConfirm.weekEnd).format('DD.MM.YY')} — ${deleteConfirm.totalAmount} ₴` : ''}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={async () => { await deleteMutation.mutateAsync(deleteConfirm.id); setDeleteConfirm(null); }}
      />
    </Stack>
  );
};

export default AdminAccountingPage;
